import { useState } from 'react'
import {
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  FileText,
  Calendar,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface WebFetchRendererProps {
  data: unknown
}

interface FetchResult {
  url?: string
  title?: string
  publish_date?: string
  excerpts?: string[]
}

interface FetchResponse {
  extract_id?: string
  results?: FetchResult[]
  // Fallback for other shapes
  content?: string
  text?: string
  result?: string
  url?: string
  [key: string]: unknown
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handle = async () => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button
      onClick={handle}
      className="p-1 rounded hover:bg-muted/50 transition-colors text-muted-foreground"
      title="Copy"
    >
      {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
    </button>
  )
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '')
  } catch {
    return url
  }
}

/** Clean excerpts: strip section headers, clean markdown links */
function cleanExcerpt(text: string): string {
  return text
    .replace(/Section Title:.*?\nContent:\n?/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function FetchResultCard({
  result,
  defaultExpanded,
}: {
  result: FetchResult
  defaultExpanded: boolean
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const url = result.url || ''
  const title = result.title || (url ? getDomain(url) : 'Page Content')
  const fullText = result.excerpts ? result.excerpts.map(cleanExcerpt).join('\n\n') : ''
  const isLong = fullText.length > 600

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs">
        <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline truncate flex items-center gap-1"
          >
            {title}
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        ) : (
          <span className="font-medium">{title}</span>
        )}
        {result.publish_date && (
          <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground ml-auto flex-shrink-0">
            <Calendar className="w-2.5 h-2.5" />
            {result.publish_date}
          </span>
        )}
      </div>

      {/* Content */}
      {fullText && (
        <div className="space-y-1">
          {isLong && (
            <div className="flex items-center justify-between">
              <div
                className="flex items-center gap-1 cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
                <span className="text-[10px] font-semibold uppercase tracking-wide">
                  Content ({fullText.length.toLocaleString()} chars)
                </span>
              </div>
              <CopyButton text={fullText} />
            </div>
          )}
          {(!isLong || expanded) && (
            <pre
              className={cn(
                'text-xs whitespace-pre-wrap break-words leading-relaxed',
                'bg-muted/20 p-3 rounded-lg border border-border/30',
                isLong && 'max-h-[400px] overflow-y-auto',
              )}
            >
              {fullText}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

export function WebFetchRenderer({ data }: WebFetchRendererProps) {
  if (!data || typeof data !== 'object') {
    if (typeof data === 'string') {
      // Try parse as JSON
      try {
        const parsed = JSON.parse(data)
        return <WebFetchRenderer data={parsed} />
      } catch {
        return (
          <pre
            className={cn(
              'text-xs whitespace-pre-wrap break-words leading-relaxed',
              'bg-muted/20 p-3 rounded-lg max-h-[400px] overflow-y-auto',
            )}
          >
            {data}
          </pre>
        )
      }
    }
    return <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>
  }

  const response = data as FetchResponse

  // Structured results from Parallel Search
  if (Array.isArray(response.results) && response.results.length > 0) {
    return (
      <div className="space-y-3">
        {response.results.map((result, i) => (
          <FetchResultCard
            key={i}
            result={result}
            defaultExpanded={response.results!.length === 1}
          />
        ))}
      </div>
    )
  }

  // Fallback: plain text content
  const textContent =
    typeof response.content === 'string'
      ? response.content
      : typeof response.text === 'string'
        ? response.text
        : typeof response.result === 'string'
          ? response.result
          : null

  if (textContent) {
    return (
      <pre
        className={cn(
          'text-xs whitespace-pre-wrap break-words leading-relaxed',
          'bg-muted/20 p-3 rounded-lg max-h-[400px] overflow-y-auto',
        )}
      >
        {textContent}
      </pre>
    )
  }

  // Last resort
  return (
    <pre className="text-xs whitespace-pre-wrap bg-muted/30 p-3 rounded-lg max-h-[300px] overflow-y-auto">
      {JSON.stringify(data, null, 2)}
    </pre>
  )
}
